import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A reusable Textarea component with Tailwind CSS styling.
 *
 * The field says where the keyboard is. It used to clear the browser's own
 * outline and set `focus:border-border-primary` — the same colour the resting
 * border already had — so every textarea in the app had no focus state at all.
 * The ring here is the one `Input.jsx` draws, so a text field and a text area
 * side by side answer the keyboard the same way.
 *
 * `ref` is a plain prop under React 19, so it arrives in `props` and needs no
 * `forwardRef`. It used to be declared as a second parameter as well, which
 * React never passes to a function component — dead, and misleading to the next
 * person who needed the node.
 *
 * @component
 * @param {object} props - Props passed to the textarea element.
 * @param {string} [props.className] - Additional class names to customize styling.
 * @param {...any} [props] - Any other standard HTML textarea attributes (value, onChange, placeholder, disabled, ref, ...).
 */
export const Textarea = ({ className, ...props }) => {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        "w-full bg-background-secondary border border-border-input rounded-lg px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted transition",
        "focus:outline-none focus-visible:outline-none focus-visible:border-tone-blue focus-visible:ring-2 focus-visible:ring-tone-blue/40",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
};
